import React from "react";
//#region types
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#region components
import { useReceiptService } from "serviceHooks";
//#endregion

function useReceiptAttachments(
  receipt: WintSuperkollAPIModel.Receipt,
  receiptFormRef?: React.RefObject<FormHandles>,
  fileUploaderFieldName?: string
) {
  const receiptService = useReceiptService();
  const [attachments, setAttachments] = React.useState<ImageMeta[]>();
  // Image ids never change content, so data is downloaded once per id. A reloaded receipt (e.g. after
  // a draft save) then shows its unchanged images at once instead of blanking and re-downloading them.
  const loadedData = React.useRef(new Map<number, string>());

  React.useEffect(() => {
    if (receipt?.attachments) {
      const attachments =
        receipt.attachments?.map<ImageMeta>((meta) => ({
          id: meta.id,
          contentType: meta.contentType,
          fileName: "",
          numberOfPages: 1,
          data: loadedData.current.get(meta.id),
        })) ?? [];
      setAttachments(attachments ?? []);

      const missing = receipt.attachments.filter((attachment) => !loadedData.current.has(attachment.id));
      if (missing.length === 0) return;

      Promise.all(
        missing.map((attachment) => {
          return receiptService.getAttachmentData(receipt.id, attachment.id).then((res) => {
            loadedData.current.set(attachment.id, res);
            return { ...attachment, data: res };
          });
        })
      ).then((attachments) => {
        const getUpdatedAttachments = (prevAttachments: ImageMeta[]) => {
          const newAttachments = prevAttachments.map((p) => ({ ...p }));
          attachments.forEach((attachment) => {
            const attachmentToUpdate = newAttachments.find((a) => a.id === attachment.id);
            if (attachmentToUpdate) {
              attachmentToUpdate.data = attachment.data;
            }
          });
          return newAttachments;
        };

        setAttachments((prevAttachments) => getUpdatedAttachments(prevAttachments ?? []));

        if (
          receiptFormRef?.current &&
          receiptFormRef.current.getValues &&
          receiptFormRef.current.setFieldValue &&
          fileUploaderFieldName
        ) {
          const values = receiptFormRef.current.getValues();
          const newFiles = [...(values[fileUploaderFieldName] ?? [])];
          receiptFormRef.current.setFieldValue(fileUploaderFieldName, getUpdatedAttachments(newFiles));
        }
      });
    }
  }, [receipt?.attachments]);

  const onUploadedFilesChange = (name: string, updatedValue: UploadedFile[]) =>
    setAttachments(
      updatedValue.map<ImageMeta>((upload) => ({
        ...upload,
        id: upload.id as number,
        numberOfPages: 1,
      }))
    );

  const getLoadedData = (attachmentId: number) => loadedData.current.get(attachmentId);

  return { attachments, onUploadedFilesChange, getLoadedData };
}

export default useReceiptAttachments;
